import contacts from './contacts';

// Global SEO Configuration (single language: English)
export const seoConfig = {
  defaultTitle: 'DELVANTE',
  titleTemplate: '%s | DELVANTE',
  defaultDescription: 'DELVANTE Global — European visa and immigration advisory. Personalised support for relocation, residency and citizenship programmes across Europe.',
  siteUrl: 'https://delvante.icode.md',
  defaultImage: '/og-image.jpg',
  twitterHandle: '@delvanteglobal',

  // Page-specific SEO configurations
  pages: {
    home: {
      title: 'DELVANTE Global | European Visa & Immigration Advisory',
      description: 'DELVANTE Global — your strategic partner for European visas and immigration. Personalised consultations for relocation, residency and citizenship programmes.',
      keywords: 'visa advisory, immigration consultancy, European visa, relocation Europe, residency programme, citizenship by investment'
    },
    contact: {
      title: 'Contact DELVANTE | Visa & Immigration Consultancy',
      description: 'Contact the DELVANTE team for visa applications and immigration consultation. We help with documentation for visas across Europe.',
      keywords: 'contact visas, visa consultancy, visa application, immigration consultation'
    },
    visa_type: {
      title: 'Visa Programme | DELVANTE',
      description: 'Explore European visa programmes with DELVANTE. Detailed requirements, processing times and expert assistance for your application.',
      keywords: 'visa types, European visa programme, visa requirements, visa application'
    },
    login: {
      title: 'Sign In | DELVANTE',
      description: 'Sign in to your DELVANTE account to manage your visa applications and documents.',
      keywords: 'login, sign in, visa account'
    },
    register: {
      title: 'Create Account | DELVANTE',
      description: 'Create your DELVANTE account to start your visa application journey.',
      keywords: 'register, create account, visa application'
    },
    forgot_password: {
      title: 'Reset Password | DELVANTE',
      description: 'Reset your DELVANTE account password securely.',
      keywords: 'reset password, forgot password, account recovery'
    },
    reset_password: {
      title: 'Set New Password | DELVANTE',
      description: 'Set a new password for your DELVANTE account.',
      keywords: 'new password, change password, account security'
    },
    verify_email: {
      title: 'Verify Email | DELVANTE',
      description: 'Verify your email address to activate your DELVANTE account.',
      keywords: 'verify email, account activation, email confirmation'
    },
    verify_token: {
      title: 'Account Activation | DELVANTE',
      description: 'Final step to activate your DELVANTE account.',
      keywords: 'account activation, token verification, account confirmation'
    },
    google_callback: {
      title: 'Google Sign-In | DELVANTE',
      description: 'Signing in to DELVANTE with your Google account.',
      keywords: 'google sign in, oauth, google login'
    },
    terms: {
      title: 'Terms and Conditions | DELVANTE',
      description: 'Terms and conditions for using DELVANTE services.',
      keywords: 'terms and conditions, service terms'
    },
    privacy: {
      title: 'Privacy Policy | DELVANTE',
      description: 'DELVANTE privacy policy. How we protect your personal data (GDPR).',
      keywords: 'privacy policy, data protection, GDPR'
    },
    profile: {
      title: 'My Profile | DELVANTE',
      description: 'Manage your DELVANTE account, applications and preferences.',
      keywords: 'account, profile, visa applications'
    },
    profile_settings: {
      title: 'Account Settings | DELVANTE',
      description: 'Manage your DELVANTE account settings and password.',
      keywords: 'account settings, password, personal data'
    },
    not_found: {
      title: 'Page Not Found (404) | DELVANTE',
      description: 'The page you are looking for does not exist. Explore our visa pathways or contact us for assistance.',
      keywords: '404, page not found, missing page'
    },
  },

  // Organization schema
  organization: {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'DELVANTE',
    alternateName: 'DELVANTE Global',
    description: 'European visa and immigration advisory. Personalised support for relocation, residency and citizenship programmes across Europe.',
    url: 'https://delvante.icode.md',
    logo: 'https://delvante.icode.md/logo.png',
    image: 'https://delvante.icode.md/og-image.jpg',
    priceRange: '$$$',
    telephone: contacts.phone.raw,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Chișinău',
      addressCountry: 'MD'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '47.0105',
      longitude: '28.8638'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: contacts.phone.raw,
      contactType: 'customer service',
      areaServed: ['MD'],
      availableLanguage: ['English'],
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59'
      }
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59'
    },
    sameAs: [
      contacts.social.facebook.url,
      contacts.social.instagram.url,
      contacts.social.tiktok.url,
      contacts.social.telegram.url
    ],
    areaServed: {
      '@type': 'Country',
      name: 'Moldova'
    },
    paymentAccepted: 'Cash, Card',
    currenciesAccepted: 'MDL, EUR, USD'
  }
};

// Helper function to get page SEO (English-only site; lang kept for backwards compatibility)
export const getPageSEO = (pageName, lang = 'en') => {
  const pageSEO = seoConfig.pages[pageName];
  if (!pageSEO) {
    return {
      title: seoConfig.defaultTitle,
      description: seoConfig.defaultDescription,
      keywords: 'visa advisory, immigration consultancy, European visa'
    };
  }
  return pageSEO;
};

// Helper function to generate canonical URL (no language prefix)
export const getCanonicalUrl = (path, lang = 'en') => {
  // Remove leading slash
  let cleanPath = path.startsWith('/') ? path.substring(1) : path;

  // Remove legacy language prefix if present (en/, ro/, ru/, de/, fr/, it/)
  cleanPath = cleanPath.replace(/^(en|ro|ru|de|fr|it)\//, '');

  return cleanPath ? `${seoConfig.siteUrl}/${cleanPath}` : `${seoConfig.siteUrl}/`;
};

// Helper function to get hreflang alternates (single language — none)
export const getHrefLangAlternates = (path) => [];
